<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="760px" scrollable>
        <v-card>
            <v-card-title>
                <span class="quaro-subheading">Import tags</span>
                <v-btn icon absolute right top class="mt-2" aria-label="Close dialog" title="Close dialog" @click="close">
                    <v-icon aria-hidden="true">mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-card-subtitle class="pt-1">
                Upload a CSV or Excel file with a keyword column and one or more tag columns. Every selected column becomes a dropdown column of this collection. Keywords that are not in
                this collection are only reported.
            </v-card-subtitle>

            <v-card-text>
                <v-file-input
                    v-model="file"
                    accept=".csv,.xlsx"
                    rounded
                    filled
                    dense
                    label="Choose CSV or Excel file (.csv, .xlsx)"
                    prepend-icon=""
                    prepend-inner-icon="mdi-file-upload-outline"
                    :loading="parsing"
                    :disabled="importing"
                    @change="parseFile" />

                <div v-if="rows.length">
                    <v-checkbox v-model="hasHeaderRow" class="mt-0" dense hide-details label="First row contains column names" @change="buildColumnConfig" />
                    <v-select v-model="keywordColumn" :items="columnItems" class="mt-4" rounded filled dense hide-details label="Keyword column" @change="keywordColumnChanged" />

                    <div class="quarolbl mt-5 mb-1">Tag columns</div>
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th style="width: 40px"></th>
                                <th>Column in file</th>
                                <th>Dropdown column in collection</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="config in tagColumnConfigs" :key="config.index">
                                <td>
                                    <v-simple-checkbox v-model="config.selected" color="primary" :aria-label="'Import ' + columnName(config.index)" @input="resetReport" />
                                </td>
                                <td>
                                    {{ columnName(config.index) }}
                                    <div class="caption grey--text">{{ config.distinct }} different values{{ config.numeric ? " · looks like numbers" : "" }}</div>
                                </td>
                                <td class="py-1">
                                    <v-text-field v-model="config.target" :disabled="!config.selected" rounded filled dense hide-details placeholder="Column name" @input="resetReport">
                                        <template v-slot:append v-if="dropdownColumns.length">
                                            <v-menu offset-y left max-height="300">
                                                <template v-slot:activator="{ on }">
                                                    <v-btn icon small v-on="on" :disabled="!config.selected" title="Use an existing dropdown column" aria-label="Use an existing dropdown column">
                                                        <v-icon small>mdi-table-column</v-icon>
                                                    </v-btn>
                                                </template>
                                                <v-list dense>
                                                    <v-subheader>Existing dropdown columns</v-subheader>
                                                    <v-list-item v-for="name in dropdownColumns" :key="name" @click="useExistingColumn(config, name)">
                                                        <v-list-item-title>{{ name }}</v-list-item-title>
                                                    </v-list-item>
                                                </v-list>
                                            </v-menu>
                                        </template>
                                    </v-text-field>
                                    <div class="caption ml-4" :class="targetState(config).color">{{ config.selected ? targetState(config).text : "" }}</div>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <div class="caption grey--text mt-1">{{ mappedRows.length }} rows in file</div>
                </div>

                <v-alert v-if="error" type="error" dense outlined class="mt-4">{{ error }}</v-alert>

                <div v-if="report" class="mt-4">
                    <v-alert :type="report.dry_run ? 'info' : 'success'" dense outlined>
                        <span v-if="report.dry_run">{{ report.keywords_tagged }} keywords will be tagged.</span>
                        <span v-else>{{ report.keywords_tagged }} keywords have been tagged.</span>
                    </v-alert>
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>Column</th>
                                <th>Keywords</th>
                                <th>Options</th>
                                <th>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="column in report.columns" :key="column.column">
                                <td>
                                    {{ column.column }}
                                    <v-chip v-if="column.column_created && column.matched" x-small class="ml-1" color="primary" outlined>new</v-chip>
                                </td>
                                <td>{{ column.matched }}</td>
                                <td>
                                    {{ column.option_count }}
                                    <span v-if="column.new_options.length" class="grey--text">({{ column.new_options.length }} new)</span>
                                </td>
                                <td class="caption">
                                    <div v-if="column.error" class="error--text">{{ column.error }}</div>
                                    <div v-if="column.skipped_empty">{{ column.skipped_empty }} empty cells skipped</div>
                                    <div v-if="column.conflict_count" class="warning--text" :title="column.conflicts.map((x) => x.keyword + ' (' + x.tags.join(' / ') + ')').join(', ')">
                                        {{ column.conflict_count }} keywords with different tags — last row wins
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <v-alert v-if="report.unknown_count" type="warning" dense outlined class="mt-3">
                        {{ report.unknown_count }} keywords are not in this collection and were ignored.
                        <v-btn x-small text color="primary" @click="showUnknown = !showUnknown">{{ showUnknown ? "hide" : "show" }}</v-btn>
                        <div v-if="showUnknown" class="caption mt-1 unknown-list">
                            {{ report.unknown_keywords.join(", ") }}<span v-if="report.unknown_count > report.unknown_keywords.length"> …</span>
                        </div>
                    </v-alert>
                </div>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn text rounded color="primary" :disabled="!canSubmit || importing" :loading="checking" @click="submit(true)">check</v-btn>
                <v-btn rounded color="primary" class="white--text" :disabled="!canSubmit || !report || !report.dry_run || !report.keywords_tagged" :loading="importing" @click="submit(false)">
                    import
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    export default {
        props: {
            value: Boolean,
            headers: Object,
            listname: String
        },
        data() {
            return {
                auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH),
                file: null,
                rows: [],
                hasHeaderRow: true,
                keywordColumn: 0,
                columnConfigs: [],
                parsing: false,
                checking: false,
                importing: false,
                error: "",
                report: null,
                showUnknown: false
            }
        },
        computed: {
            columnCount() {
                return this.rows.reduce((max, row) => (row.length > max ? row.length : max), 0)
            },
            dataRows() {
                return this.hasHeaderRow ? this.rows.slice(1) : this.rows
            },
            columnItems() {
                let items = []
                for (let index = 0; index < this.columnCount; index++) items.push({ text: this.columnName(index), value: index })
                return items
            },
            tagColumnConfigs() {
                return this.columnConfigs.filter((config) => config.index !== this.keywordColumn)
            },
            selectedConfigs() {
                return this.tagColumnConfigs.filter((config) => config.selected)
            },
            dropdownColumns() {
                let list = this.headers && Array.isArray(this.headers.modifiedHeaders) ? this.headers.modifiedHeaders : []
                return list.filter((header) => header && header.custom && header.customOptions && header.customOptions.type == "Dropdown").map((header) => header.text)
            },
            customColumnNames() {
                let list = this.headers && Array.isArray(this.headers.modifiedHeaders) ? this.headers.modifiedHeaders : []
                return list.filter((header) => header && header.custom).map((header) => header.text)
            },
            mappedRows() {
                return this.dataRows.map((row) => ({ keyword: row[this.keywordColumn] || "", tags: this.selectedConfigs.map((config) => row[config.index] || "") }))
            },
            canSubmit() {
                if (!this.mappedRows.length || !this.selectedConfigs.length) return false
                let names = this.selectedConfigs.map((config) => this.targetName(config))
                return names.every((name) => name.length >= 3) && new Set(names).size === names.length
            }
        },
        methods: {
            columnName(index) {
                let name = this.hasHeaderRow && this.rows[0] && this.rows[0][index] ? String(this.rows[0][index]).trim() : ""
                return name ? name : "Column " + (index + 1)
            },
            targetName(config) {
                return typeof config.target == "string" ? config.target.trim() : ""
            },
            useExistingColumn(config, name) {
                config.target = name
                this.resetReport()
            },
            targetState(config) {
                let name = this.targetName(config)
                if (name.length < 3) return { text: "At least 3 characters", color: "error--text" }
                if (this.selectedConfigs.filter((other) => this.targetName(other) === name).length > 1) return { text: "Used twice", color: "error--text" }
                if (this.dropdownColumns.includes(name)) return { text: "Existing column — missing options are added", color: "grey--text" }
                if (this.customColumnNames.includes(name)) return { text: "Existing column is not a dropdown", color: "error--text" }
                return { text: "New dropdown column", color: "grey--text" }
            },
            resetReport() {
                this.report = null
                this.error = ""
                this.showUnknown = false
            },
            reset() {
                this.file = null
                this.rows = []
                this.hasHeaderRow = true
                this.keywordColumn = 0
                this.columnConfigs = []
                this.resetReport()
            },
            close() {
                this.reset()
                this.$emit("input", false)
            },
            // one entry per file column: numeric columns (e.g. search volume) start unselected
            buildColumnConfig() {
                this.resetReport()
                let names = this.hasHeaderRow ? (this.rows[0] || []).map((name) => String(name).trim().toLowerCase()) : []
                let keywordIndex = names.findIndex((name) => ["keyword", "keywords", "suchbegriff", "query"].includes(name))
                this.keywordColumn = keywordIndex > -1 ? keywordIndex : 0
                let configs = []
                for (let index = 0; index < this.columnCount; index++) {
                    let values = this.dataRows.map((row) => String(row[index] || "").trim()).filter((cell) => cell !== "")
                    let numericCount = values.filter((cell) => /^-?[\d.,]+$/.test(cell)).length
                    let numeric = values.length > 0 && numericCount / values.length > 0.8
                    let name = this.hasHeaderRow ? this.columnName(index) : ""
                    configs.push({ index: index, selected: !numeric && values.length > 0, target: name, distinct: new Set(values).size, numeric: numeric })
                }
                this.columnConfigs = configs
            },
            keywordColumnChanged() {
                this.resetReport()
            },
            parseFile(file) {
                this.rows = []
                this.columnConfigs = []
                this.resetReport()
                if (!file) return
                let type = file.name.split(".").pop().toLowerCase()
                if (type !== "csv" && type !== "xlsx") {
                    this.error = "Please upload a .csv or .xlsx file. Old .xls files have to be saved as .xlsx first."
                    return
                }
                if (file.size > 10 * 1024 * 1024) {
                    this.error = "The file is larger than 10 MB."
                    return
                }
                let formData = new FormData()
                formData.append("file", file)
                this.parsing = true
                this.$http
                    .post(process.env.VUE_APP_APIURL + "/research/keywords/tags/parse", formData, { headers: { Authorization: this.auth } })
                    .then((response) => {
                        this.rows = response.body.rows || []
                        if (!this.rows.length) this.error = "The file is empty."
                        else if (this.columnCount < 2) this.error = "The file needs at least two columns: keyword and tag."
                        this.buildColumnConfig()
                    })
                    .catch((response) => {
                        this.error = (response.body && response.body.error) || "The file could not be read."
                    })
                    .finally(() => {
                        this.parsing = false
                    })
            },
            submit(dryRun) {
                this.error = ""
                if (dryRun) this.checking = true
                else this.importing = true
                this.$http
                    .post(
                        process.env.VUE_APP_APIURL + "/research/keywords/tags/import",
                        {
                            company_id: localStorage.company_id,
                            listname: this.listname,
                            columns: this.selectedConfigs.map((config) => this.targetName(config)),
                            rows: this.mappedRows,
                            dry_run: dryRun
                        },
                        { headers: { Authorization: this.auth } }
                    )
                    .then((response) => {
                        this.report = response.body
                        if (!dryRun) {
                            this.$emit("imported", response.body)
                            this.close()
                        }
                    })
                    .catch((response) => {
                        this.error = (response.body && response.body.error) || "The tags could not be imported."
                        this.report = (response.body && response.body.report) || null
                    })
                    .finally(() => {
                        this.checking = false
                        this.importing = false
                    })
            }
        }
    }
</script>

<style scoped>
    .unknown-list {
        max-height: 120px;
        overflow-y: auto;
    }
</style>
